<template>
  <div>
    <div ref="form" class="bg-white w-full">
      <template v-for="(section, sectionName) in machineData" :key="sectionName">
        <CollapsibleSection
          :title="sectionName"
          :is-open-by-default="isSectionOpenByDefault(sectionName)"
          @section-opened="handleSectionOpened"
        >
          <template v-for="field in section" :key="field.db_name">
            <Component
              :is="field.component"
              v-model="formData[field.db_name]"
              :name="field.db_name"
              :label="field.name + (field.unit ? ` (${field.unit})` : '') + (field.required ? ' *' : '')"
              :options="getFieldOptions(field)"
              :type="field.field"
              :disabled="field.name === 'Id'"
              :error="errors[field.db_name]"
              :is-percent="field.isPercent"
              :multiple="field.multiple"
              top-label
              @change="handleFieldChange(field.db_name, field.component, $event)"
            />
          </template>
        </CollapsibleSection>
      </template>
    </div>

    <div class="flex justify-end m-20 gap-8">
      <SparkButton variant="outlined" @click="$emit('close')">
        <div v-text="'Cancel'" />
      </SparkButton>
      <SparkButton variant="secondary" type="button" @click="validateFields">
        <div v-text="mode === 'add' ? 'Add Machine' : 'Save'" />
      </SparkButton>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { z } from 'zod';

import { generateMachineSchema, addCustomMachineForm } from '@/utils/machineform';
import CollapsibleSection from '@/views/Library/material/components/CollapsibleSection.vue';
import SparkButton from '@/components/SparkComponents/SparkButton.vue';
import SparkForm from '@/components/SparkComponents/SparkForm.vue';
import SparkInput from '@/components/SparkComponents/SparkInput.vue';
import SparkSelect from '@/components/SparkComponents/SparkSelect.vue';
import getCurrentDateWithTime from '@/composables/dateFormatting.js';
import { transformObjectToArray } from '@/helpers';

export default {
  name: 'CustomMachineForm',

  components: {
    CollapsibleSection,
    SparkButton,
    SparkForm,
    SparkInput,
    SparkSelect,
  },

  props: {
    machineData: { type: Object, required: true },
    show: { type: Boolean, required: true },
    mode: { type: String, required: true },
    availableProcesses: { type: Object, default: () => ({}) },
  },

  emits: ['add', 'close', 'edit', 'tech-changed'],

  data() {
    return {
      formData: {},
      errors: {},
      openSection: 'General',
    };
  },

  computed: {
    ...mapState(['user', 'curTechs']),

    updatedCurTechs() {
      return transformObjectToArray(this.curTechs);
    },

    updatedProcesses() {
      const selectedTech = this.formData.tech;
      if (selectedTech && this.availableProcesses[selectedTech]) {
        return this.availableProcesses[selectedTech];
      }
      return [];
    },

    updatedFilteredRestrictions() {
      const userRole = this.user.user_role;
      if (userRole === 'user') {
        return [{ label: 'Owner', value: 'owner' }];
      } else {
        return [
          { label: 'Owner', value: 'owner' },
          { label: 'My Organization', value: 'organization' },
        ];
      }
    },

    schema() {
      return generateMachineSchema(addCustomMachineForm, this.formData.tech);
    },
  },

  watch: {
    show: {
      handler(value) {
        if (value && Object.keys(this.machineData).length > 0) {
          this.formData = {};
          this.errors = {};
          Object.values(this.machineData).forEach(section => {
            section.forEach(field => {
              if (this.mode === 'add') {
                // For "Add similar machine", copy value except for the primary key
                if (field.db_name === 'mac_id') {
                  this.formData[field.db_name] = null;
                } else if (field.db_name === 'mac_name') {
                  // If original name exists, append ' (Copy)' to differentiate
                  this.formData[field.db_name] = field.value ? `${field.value} (Copy)` : '';
                } else {
                  this.formData[field.db_name] = field.value;
                }
              } else {
                this.formData[field.db_name] = field.value;
              }
            });
          });
        }
      },
      immediate: true,
    },

    'formData.tech': {
      handler(newValue, oldValue) {
        if (newValue && newValue !== oldValue) {
          this.openSection = this.getSectionForTech(newValue);
          // Reset the process selection when technology changes
          this.formData.prc_uid = '';
          // Emit an event to notify parent components that the technology has changed
          this.$emit('tech-changed', newValue);
        }
      },
      immediate: true,
    },
  },

  methods: {
    getFieldOptions(field) {
      if (field.db_name === 'tech') {
        return this.updatedCurTechs;
      } else if (field.db_name === 'prc_uid') {
        return this.updatedProcesses;
      } else if (field.db_name === 'restriction') {
        return this.updatedFilteredRestrictions;
      }
      return Array.isArray(field.options) && field.options.length > 0 ? field.options : [];
    },

    handleSubmit() {
      if (this.mode === 'add') {
        this.formData = this.generateMacNameIfNotGiven(this.formData);
        if (this.formData.mac_id) {
          delete this.formData.mac_id;
        }
        this.$emit('add', this.formData);
      } else {
        this.$emit('edit', this.formData);
      }
    },

    generateMacNameIfNotGiven(formData) {
      if (formData === undefined) return formData;
      if (!formData.mac_name) {
        let dateString = getCurrentDateWithTime().dateWithTime;
        formData.mac_name = this.user.first_name + ' ' + this.user.last_name + ' ' + dateString;
      }
      return formData;
    },

    validateFields() {
      const result = this.schema.safeParse(this.formData);
      const errors = result.error?.issues;
      if (!errors) {
        this.handleSubmit();
        return;
      }

      this.errors = errors.reduce((acc, issue) => {
        acc[issue.path[0]] = issue.message;
        return acc;
      }, {});

      this.$refs.form.scrollIntoView({ behavior: 'smooth' });
    },

    validateField(field) {
      if (!this.schema.shape[field]) {
        return;
      }
      const result = this.schema.safeParse(this.formData);
      const errors = result.error?.issues;
      if (errors) {
        this.errors[field] = errors.find(issue => issue?.path[0] === field)?.message || '';
      } else {
        this.errors[field] = '';
      }
    },

    getSectionForTech(tech) {
      switch (tech) {
        case 'milling':
          return 'Milling';
        case 'casting':
          return 'Casting';
        default:
          return 'General';
      }
    },

    isSectionOpenByDefault(sectionName) {
      return sectionName === this.openSection;
    },

    handleSectionOpened(sectionName) {
      this.openSection = sectionName;
    },

    handleFieldChange(fieldName, component, value) {
      if (component === 'SparkMultipleSelection') {
        this.formData[fieldName] = value;
      }
      this.validateField(fieldName);
    },

    populateMachineFormValues(data) {
      if (!data) return;

      this.formData = {};
      this.errors = {};
      Object.values(this.machineData).forEach(section => {
        section.forEach(field => {
          if (this.mode === 'add') {
            // For "Add similar machine", copy value except for the primary key
            if (field.db_name === 'mac_id') {
              this.formData[field.db_name] = null;
            } else if (field.db_name === 'mac_name') {
              // If original name exists, append ' (Copy)' to differentiate
              this.formData[field.db_name] = data[field.db_name] ? `${data[field.db_name]} (Copy)` : '';
            } else if (field.db_name === 'prc_uid') {
              // Handle process UID and set the corresponding synonym
              const defaultProcess = data[field.db_name];
              if (defaultProcess && field.options) {
                const process = field.options.find(opt => opt.value === defaultProcess);
                this.formData[field.db_name] = process ? process.value : '';
              } else {
                this.formData[field.db_name] = '';
              }
            } else {
              this.formData[field.db_name] = data[field.db_name];
            }
          } else {
            if (field.db_name === 'prc_uid') {
              // Handle process UID and set the corresponding synonym
              const defaultProcess = data[field.db_name];
              if (defaultProcess && field.options) {
                const process = field.options.find(opt => opt.value === defaultProcess);
                this.formData[field.db_name] = process ? process.value : '';
              } else {
                this.formData[field.db_name] = '';
              }
            } else {
              this.formData[field.db_name] = data[field.db_name];
            }
          }
        });
      });
    },

    assignValues(data) {
      if (!data) return;

      this.formData = {};
      this.errors = {};
      Object.values(this.machineData).forEach(section => {
        section.forEach(field => {
          if (this.mode === 'add') {
            // For "Add similar machine", copy value except for the primary key
            if (field.db_name === 'mac_id') {
              this.formData[field.db_name] = null;
            } else if (field.db_name === 'mac_name') {
              // If original name exists, append ' (Copy)' to differentiate
              const value = data.general?.[field.db_name] || data[field.db_name];
              this.formData[field.db_name] = value ? `${value} (Copy)` : '';
            } else if (field.db_name === 'prc_uid') {
              // Handle process UID and set the corresponding synonym
              const defaultProcess = data.general?.[field.db_name] || data[field.db_name];
              if (defaultProcess && field.options) {
                const process = field.options.find(opt => opt.value === defaultProcess);
                this.formData[field.db_name] = process ? process.value : '';
              } else {
                this.formData[field.db_name] = '';
              }
            } else {
              this.formData[field.db_name] = data.general?.[field.db_name] || data[field.db_name];
            }
          } else {
            if (field.db_name === 'prc_uid') {
              // Handle process UID and set the corresponding synonym
              const defaultProcess = data.general?.[field.db_name] || data[field.db_name];
              if (defaultProcess && field.options) {
                const process = field.options.find(opt => opt.value === defaultProcess);
                this.formData[field.db_name] = process ? process.value : '';
              } else {
                this.formData[field.db_name] = '';
              }
            } else {
              this.formData[field.db_name] = data.general?.[field.db_name] || data[field.db_name];
            }
          }
        });
      });
    },
  },
};
</script>

<style>
.vue-multiselect-custom .multiselect__option {
  color: #000;
  font-weight: normal;
  padding: 8px 12px;
  display: block;
}

.vue-multiselect-custom .multiselect__option--highlight {
  background: #f0f0f0;
  color: #000;
}

.vue-multiselect-custom .multiselect__option--selected {
  background: #e0e0e0;
  color: #000;
  font-weight: bold;
}

.vue-multiselect-custom .multiselect__tag {
  background: #e0e0e0;
  color: #000;
}

.vue-multiselect-custom .multiselect__single {
  color: #000;
}
</style>
